/**
 * fab-size-picker.js
 *
 * Drives the new size picker. Two responsibilities:
 *
 *  1. CONTRACT — Translate pill clicks into a real <select>.value change
 *     plus a `change` event, so product-form.js / frame-price-updater.js /
 *     the rest of the variant pipeline keep working unchanged.
 *
 *  2. AESTHETICS — Smooth, Apple/Airbnb-style transitions:
 *       - The room mock expands by measuring its natural height first
 *         (FLIP-ish), animating to that exact number, then snapping to
 *         auto so subsequent layout changes don't cause re-animation.
 *       - CSS placeholder scene swaps cross-fade rather than yank.
 *       - The print width is set as a % of the stage so it matches the
 *         physical ratio print-width (cm) / assumed wall width (cm) for
 *         each scene. The art image is an inner <img> fed from the matching
 *         variant's featured_image (see data-variants on variant-selects).
 *       - The readout pill is positioned from measured print geometry so it
 *         sits just above the print's top edge.
 *
 * Scene mapping is by raw cm-width — see SCENE_FOR_CM.
 *
 * ROOM COMPOSITOR — when a real room library exists (metaobject-backed),
 * the canvas compositor (fab-room-compositor.js) draws a photographed room
 * with the artwork composited in at honest scale. If the compositor isn't
 * loaded, the library is empty, or any image fails, the picker silently
 * falls back to the CSS placeholder scenes below.
 */

(function () {
  'use strict';

  // ============================================================
  // Scene mapping
  // ============================================================
  // Tuned by short edge / width. cm number derived from the raw
  // option value (e.g. "15x20cm" → 15).
  function sceneForCm(cmWidth) {
    if (cmWidth == null || isNaN(cmWidth)) return 'sideboard';
    if (cmWidth <= 28)  return 'desk';
    if (cmWidth <= 45)  return 'sideboard';
    if (cmWidth <= 60)  return 'sofa';
    return 'hero';
  }

  /* =====================================================================
     ROOM LIBRARY — helpers for fab-size-picker (metaobject-backed rooms).
     Compositor consumes this later; pickRoomScene() selects a scene row,
     or collectExactSizeScenes() + carousel when several rows share a size.
     ===================================================================== */

  // Normalise a room string for matching. The custom.room metafield is
  // inconsistently cased across the catalogue ("Living Room" vs
  // "living room") and "Study Room" should behave as "office".
  function normaliseRoom(raw) {
    if (!raw) return '';
    const r = String(raw).toLowerCase().trim();
    if (r === 'study room' || r === 'study') return 'office';
    return r;
  }

  // Parse the room library blob emitted by the Liquid serialiser.
  // Returns an array of scene objects, each with wall_area parsed out of
  // wall_area_raw. Never throws — returns [] on any problem.
  function readRoomLibrary(root) {
    const el =
      (root || document).querySelector('[data-fab-room-library]') ||
      document.querySelector('[data-fab-room-library]');
    if (!el) return [];
    let raw;
    try {
      raw = JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(raw)) return [];

    return raw
      .map(function (s) {
        let wa = {
          x: 0,
          y: 0,
          width: 100,
          height: 100,
          photoCm: 0,
          wallRgb: null,
          wallLum: null,
        };
        try {
          const parsed =
            typeof s.wall_area_raw === 'string' ? JSON.parse(s.wall_area_raw) : s.wall_area_raw;
          if (parsed && typeof parsed === 'object') {
            wa = {
              x: Number(parsed.x) || 0,
              y: Number(parsed.y) || 0,
              width: Number(parsed.width) || 100,
              height: Number(parsed.height) || 100,
              // photoCm — the room photo's real edge-to-edge width in cm.
              // Set once when the scene is marked. Drives the moulding
              // scale. 0 if absent (old scenes).
              photoCm: Number(parsed.photoCm) || 0,

              // Optional room integration values from wall_area JSON.
              // Example: "wallRgb":[150,136,119],"wallLum":0.54
              wallRgb: Array.isArray(parsed.wallRgb)
                ? parsed.wallRgb.map(function (v) {
                    return Number(v);
                  })
                : null,
              wallLum:
                parsed.wallLum != null && !isNaN(Number(parsed.wallLum))
                  ? Number(parsed.wallLum)
                  : null,
            };
          }
        } catch (e) {
          /* keep default full-bleed box */
        }
        console.log('[room wall area parsed]', wa);
        return {
          id: s.id || '',
          roomType: normaliseRoom(s.room_type),
          // The metaobject field is still keyed `size_class` internally,
          // but now holds an exact size like "50x70" — one scene per size.
          size: normaliseSizeKey(coerceSceneSizeClass(s.size_class)),
          image: s.image || '',
          wallArea: wa,
          lightDirection: String(s.light_direction || 'ambient').toLowerCase().trim(),
          lightingImage: s.lighting_image || '',
        };
      })
      .filter(function (s) {
        // A usable scene must have an image and a resolved size.
        return s.image && s.size;
      });
  }

  // Parse the product's recommended rooms blob. Returns a normalised,
  // de-duplicated array, e.g. ["living room","office"].
  function readProductRooms(root) {
    const el =
      (root || document).querySelector('[data-fab-product-rooms]') ||
      document.querySelector('[data-fab-product-rooms]');
    if (!el) return [];
    let raw;
    try {
      raw = JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(raw)) return [];
    const seen = {};
    const out = [];
    raw.forEach(function (r) {
      const n = normaliseRoom(r);
      if (n && !seen[n]) {
        seen[n] = true;
        out.push(n);
      }
    });
    return out;
  }

  // A-size short-edge widths in cm (ISO 216).
  const ASIZE_CM = {
    A0: 84.1, A1: 59.4, A2: 42.0, A3: 29.7, A4: 21.0, A5: 14.8,
    A6: 10.5, A7: 7.4
  };

  // Size advice — line1 = verdict, line2 = counter. One variant (short /
  // test / creative) is chosen per page view and used for every size.
  const SIZE_ADVICE = {
    A5: {
      short: {
        line1: 'Best for shelves, ledges and small framed moments.',
        line2: 'Use several together for a gallery wall with presence.',
      },
      test: {
        line1: 'Small, flexible and easy to place.',
        line2: 'Works best layered, paired or mixed with larger prints.',
      },
      creative: {
        line1: 'Ideal for ledges, corners and layered displays.',
        line2: 'Mix a few into a larger gallery wall.',
      },
    },
    A4: {
      short: {
        line1: 'Good for desks, bedsides, shelves and hallway groups.',
        line2: 'Pair two or three for more wall presence.',
      },
      test: {
        line1: 'Easy to use in small rooms, nooks and reading corners.',
        line2: 'Above bigger furniture, use it as part of a set.',
      },
      creative: {
        line1: 'A strong starter size for gallery walls.',
        line2: 'Mix with A3 or A2 pieces for a more collected look.',
      },
    },
    A3: {
      short: {
        line1: 'Good for hallways, sideboards and smaller walls.',
        line2: 'Use as a pair above a console or in a tidy grid.',
      },
      test: {
        line1: 'Large enough to stand alone in a nook or hallway.',
        line2: 'Above wider furniture, add a second print or size up.',
      },
      creative: {
        line1: 'Works well in pairs, grids and gallery walls.',
        line2: 'Try two above twin beds or three down a hallway.',
      },
    },
    A2: {
      short: {
        line1: 'A useful mid-large size for bedrooms and medium walls.',
        line2: 'For sofas or beds, consider A1 or a pair.',
      },
      test: {
        line1: 'Strong enough for an entry bench, console or dresser.',
        line2: 'If the wall is wide, give it company.',
      },
      creative: {
        line1: 'Good as a single anchor or a matched pair.',
        line2: 'Hang two side by side for a clean hotel-style layout.',
      },
    },
    A1: {
      short: {
        line1: 'Strong above beds, sofas and main furniture.',
        line2: 'On extra-wide walls, use two together or go A0.',
      },
      test: {
        line1: 'Big enough to feel intentional on a main wall.',
        line2: 'For narrow corners or shelves, consider sizing down.',
      },
      creative: {
        line1: 'Works well as a bold centrepiece.',
        line2: 'Use two for a calm, symmetrical wide-wall layout.',
      },
    },
    A0: {
      short: {
        line1: 'Best as a main piece on a large wall.',
        line2: 'Leave space around it, or pair only on very wide walls.',
      },
      test: {
        line1: 'A serious statement size with real wall presence.',
        line2: 'In tighter rooms, A1 will feel easier.',
      },
      creative: {
        line1: 'Use one bold piece instead of a busy gallery wall.',
        line2: 'Or hang a matched pair across a very wide room.',
      },
    },
    '8x6': {
      short: {
        line1: 'Best for shelves, desks and picture ledges.',
        line2: 'Use several together for a gallery wall with real presence.',
      },
      test: {
        line1: 'Small, flexible and easy to layer.',
        line2: 'For a sofa or bed, use it as part of a set.',
      },
      creative: {
        line1: 'Ideal for ledges, corners and layered displays.',
        line2: 'Mix with larger prints to build a fuller gallery wall.',
      },
    },
    '14x11': {
      short: {
        line1: 'Good for bedside spaces, shelves and narrow walls.',
        line2: 'Pair two to fill more width.',
      },
      test: {
        line1: 'Easy above a desk, nightstand or small console.',
        line2: 'For a sofa, use it as part of a pair or gallery wall.',
      },
      creative: {
        line1: 'Strong in pairs for stairs, hallways or bedsides.',
        line2: 'Also works neatly inside a larger gallery wall.',
      },
    },
    '16x12': {
      short: {
        line1: 'Good for smaller walls, desks and grouped layouts.',
        line2: 'Above wider furniture, use a pair or size up.',
      },
      test: {
        line1: 'A useful size for sideboards and reading corners.',
        line2: 'If the wall is wide, give it company.',
      },
      creative: {
        line1: 'Works well as a matched pair.',
        line2: 'Try two over a console or three down a hallway.',
      },
    },
    '20x16': {
      short: {
        line1: 'A versatile mid-size for desks, consoles and smaller walls.',
        line2: 'For a sofa or bed, consider sizing up.',
      },
      test: {
        line1: 'Big enough to stand alone in tighter rooms.',
        line2: 'Pair it with a second print for more width.',
      },
      creative: {
        line1: 'Good beside a window, bookcase or low dresser.',
        line2: 'Use two together for a clean, balanced set.',
      },
    },
    '24x18': {
      short: {
        line1: 'Good above desks, consoles and smaller sofas.',
        line2: 'Also works well as a pair on a wider wall.',
      },
      test: {
        line1: 'Large enough to read clearly across the room.',
        line2: 'Above a bed, check the wall width before choosing.',
      },
      creative: {
        line1: 'A solid anchor piece for a small living room.',
        line2: 'Try two side by side if the wall feels long.',
      },
    },
    '28x20': {
      short: {
        line1: 'Good for bedrooms and living rooms without taking over.',
        line2: 'For a main piece above a bed or sofa, consider sizing up.',
      },
      test: {
        line1: 'Works as a confident single piece on medium walls.',
        line2: 'Pair two together if you want a wider look.',
      },
      creative: {
        line1: 'Strong over a dresser, loveseat or entry bench.',
        line2: 'Also works well in a clean two-print set.',
      },
    },
    '32x24': {
      short: {
        line1: 'A strong focal piece above sofas and beds.',
        line2: 'On very wide walls, use two together or go larger.',
      },
      test: {
        line1: 'Big enough to hold its own above main furniture.',
        line2: 'For narrow walls or shelves, consider sizing down.',
      },
      creative: {
        line1: 'Works well as a centrepiece.',
        line2: 'Hang two for a balanced wide-wall layout.',
      },
    },
    '40x28': {
      short: {
        line1: 'Best above beds, sofas and wide walls.',
        line2: 'In tighter spaces, consider sizing down.',
      },
      test: {
        line1: 'A strong single piece when you want real wall presence.',
        line2: 'For extra-wide walls, pair it with a second print.',
      },
      creative: {
        line1: 'Made for a main wall or large headboard.',
        line2: 'Build a calm two-print layout across a wide room.',
      },
    },
    '90x120': {
      short: {
        line1: 'A proper centrepiece for sofas, beds and large walls.',
        line2: 'Best with breathing room around it, not squeezed into a tight space.',
      },
      test: {
        line1: 'Large enough to change the whole room.',
        line2: 'Use as one strong piece instead of a busy gallery wall.',
      },
      creative: {
        line1: 'Big, confident and gallery-like.',
        line2: 'Anchor it with furniture below, or build smaller prints around it.',
      },
    },
    '100x150': {
      short: {
        line1: 'A true statement piece for big walls and spacious rooms.',
        line2: 'Best above large sofas, beds or sideboards with room to breathe.',
      },
      test: {
        line1: 'Made for real wall presence.',
        line2: 'In smaller rooms, 120×90cm will usually feel easier.',
      },
      creative: {
        line1: 'Use one piece to hold the whole wall.',
        line2: 'For very wide rooms, pair with smaller prints rather than crowding it.',
      },
    },
  };

  // Standard print sizes (cm WxH, max×min) → inch-style advice keys above.
  const CM_TO_STD_ADVICE = {
    '20x15': '8x6',
    '35x28': '14x11',
    '40x30': '16x12',
    '50x40': '20x16',
    '60x45': '24x18',
    '70x50': '28x20',
    '80x60': '32x24',
    '100x70': '40x28',
    '120x90': '90x120',
    '150x100': '100x150',
  };

  const SIZE_ADVICE_VARIANTS = ['short', 'test', 'creative'];

  function pickAdviceVariant() {
    return SIZE_ADVICE_VARIANTS[Math.floor(Math.random() * SIZE_ADVICE_VARIANTS.length)];
  }

  /** Inch advice key: larger×smaller (e.g. 6×8in → "8x6"). */
  function inchAdviceKey(inPart) {
    if (!inPart) return null;
    const m = String(inPart)
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    const a = parseFloat(m[1]);
    const b = parseFloat(m[2]);
    if (!(a > 0) || !(b > 0)) return null;
    return Math.round(Math.max(a, b)) + 'x' + Math.round(Math.min(a, b));
  }

  function cmDimsAdviceKey(cmW, cmH) {
    if (cmW == null || cmH == null) return null;
    const hi = Math.round(Math.max(cmW, cmH));
    const lo = Math.round(Math.min(cmW, cmH));
    return CM_TO_STD_ADVICE[hi + 'x' + lo] || null;
  }

  /** Map a cm short-edge to the closest A-size key that has advice copy. */
  function closestAdviceAsize(cm) {
    const keys = Object.keys(SIZE_ADVICE).filter(function (k) {
      return k.charAt(0) === 'A';
    });
    let best = null;
    let bestDiff = Infinity;
    keys.forEach(function (k) {
      const ref = ASIZE_CM[k];
      if (ref == null) return;
      const d = Math.abs(ref - cm);
      if (d < bestDiff) {
        bestDiff = d;
        best = k;
      }
    });
    return best;
  }

  function stdAdviceKey(parsed, raw) {
    if (!parsed || parsed.kind !== 'std') return null;
    const parts = String(raw || '').split('/').map(function (p) {
      return p.trim();
    });
    const fromIn = inchAdviceKey(parts[1] || '');
    if (fromIn && SIZE_ADVICE[fromIn]) return fromIn;
    const fromCm = cmDimsAdviceKey(parsed.cm, parsed.cmH);
    if (fromCm && SIZE_ADVICE[fromCm]) return fromCm;
    if (parsed.cm != null) return closestAdviceAsize(parsed.cm);
    return null;
  }

  function adviceKeyForCell(cell) {
    if (!cell) return null;
    const stamped = cell.dataset.fabAsizeName;
    if (stamped && SIZE_ADVICE[stamped]) return stamped;
    const parsed = cell.__fabParsed;
    if (parsed && parsed.kind === 'asize' && parsed.name && SIZE_ADVICE[parsed.name]) {
      return parsed.name;
    }
    if (parsed && parsed.kind === 'std') {
      return stdAdviceKey(parsed, cell.dataset.fabSizeValue);
    }
    return null;
  }

  // Placeholder when a variant has no featured image (<img src>).
  const FALLBACK_PRINT_DATA_URI =
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 40"><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#365a3b"/><stop offset="1" stop-color="#264428"/></linearGradient></defs><rect width="30" height="40" fill="url(#g)"/><circle cx="8" cy="12" r="3.5" fill="#e8c563"/><circle cx="20" cy="10" r="2.8" fill="#d97474"/><circle cx="13" cy="20" r="2.4" fill="#eea0a0"/><circle cx="22" cy="22" r="3" fill="#c068a8"/><circle cx="6" cy="28" r="2.2" fill="#f5d987"/><circle cx="17" cy="32" r="2.6" fill="#e08aab"/><circle cx="24" cy="34" r="1.8" fill="#f0c660"/><circle cx="9" cy="35" r="1.9" fill="#d97474"/></svg>'
    );

  /** Match frame-price-updater.js — variant.price is in cents. */
  function formatMoneyCents(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      const moneyFormat =
        (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return Shopify.formatMoney(cents, moneyFormat);
    }
    const mainPrice = document.querySelector('.price .price-item');
    if (mainPrice) {
      const format = mainPrice.textContent.replace(/[\d\s.,]/g, '');
      const amount = (cents / 100).toFixed(2);
      return format + amount;
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Wall-width assumption per scene (cm) — used for the caption
  // "wall 2.8m wide" line.
  const WALL_CM_BY_SCENE = {
    desk:      140,
    sideboard: 280,
    sofa:      320,
    hero:      360,
  };

  // Vertical baseline (where the BOTTOM of the print sits in the scene)
  // as a percentage from the top of the stage.
  const BASELINE_PCT_BY_SCENE = {
    desk:      62,
    sideboard: 48,
    sofa:      62,
    hero:      80,
  };

  // Scene labels (only English for now; production should add locale variants)
  const SCENE_LABEL = {
    desk:      'Desk scene',
    sideboard: 'Sideboard scene',
    sofa:      'Sofa scene',
    hero:      'Full wall scene',
  };

  // Scene HTML — sit inside the .fab-size__scene layer.
  // Kept as simple CSS shapes until production room photos exist.
  function sceneHTML(scene) {
    switch (scene) {
      case 'desk':
        return `
          <div class="fab-scene-desk-surface"></div>
          <div class="fab-scene-desk-mug"></div>
          <div class="fab-scene-desk-book"></div>
        `;
      case 'sideboard':
        return `
          <div class="fab-scene-sideboard-lamp"></div>
          <div class="fab-scene-sideboard-top"></div>
          <div class="fab-scene-sideboard-vase"></div>
        `;
      case 'sofa':
        return `
          <div class="fab-scene-sofa"></div>
          <div class="fab-scene-sofa-cushion fab-scene-sofa-cushion--left"></div>
          <div class="fab-scene-sofa-cushion fab-scene-sofa-cushion--right"></div>
        `;
      case 'hero':
        return `
          <div class="fab-scene-hero-chair"></div>
          <div class="fab-scene-hero-plant"></div>
        `;
    }
    return '';
  }

  // ============================================================
  // Parsing the option value
  // ============================================================
  // Values look like "15x20cm / 6x8in" or "A4 / A4".
  function parseSizeValue(raw) {
    if (!raw) return null;
    const parts = String(raw).split('/').map(p => p.trim());
    const cmPart = parts[0] || '';
    const inPart = parts[1] || '';

    // A-size check: starts with A followed by a digit
    const upper = cmPart.toUpperCase();
    const aMatch = upper.match(/^A\d+/);
    if (aMatch) {
      const aName = aMatch[0];
      const cm = ASIZE_CM[aName] || null;
      return {
        kind: 'asize',
        cm: cm,
        // ISO 216 portrait: long edge = short edge × √2
        ratio: Math.SQRT2,
        name: aName,
        raw: raw,
      };
    }

    // Standard "15x20cm" — parse w and h (accept × / Unicode multiply as x)
    const cmNorm = String(cmPart).replace(/\u00d7/g, 'x').replace(/×/g, 'x');
    const m = cmNorm.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (m) {
      const w = parseFloat(m[1]);
      const h = parseFloat(m[2]);
      return {
        kind: 'std',
        cm: w,
        cmH: h,
        ratio: w && h ? w / h : null,
        raw: raw,
      };
    }

    return { kind: 'unknown', cm: null, ratio: null, raw: raw };
  }

  // Normalised size key for matching a variant size against a room_scene's
  // Size field. Includes orientation, because portrait and landscape are
  // separate room_scene entries (a landscape print is used differently).
  // Standard sizes only — A-sizes have no room scenes.
  //   ("50 x 70cm / 20 x 28in", "portrait")  → "50x70-portrait"
  //   ("15x20cm / 6x8\"",        "landscape") → "15x20-landscape"
  // The room_scene Size field stores exactly this form.
  // Returns '' for A-sizes / unknowns (→ no room scene, CSS fallback).
  function sizeKey(parsed, orientation) {
    if (!parsed) return '';
    const ori = orientation === 'landscape' ? 'landscape' : 'portrait';
    if (parsed.kind === 'asize' && parsed.name) {
      // A-size key, e.g. "a3-portrait" — matches the room_scene size_class.
      return parsed.name.toLowerCase() + '-' + ori;
    }
    if (parsed.kind !== 'std') return '';
    if (parsed.cm == null || parsed.cmH == null) return '';
    // Drop any decimals — catalogue sizes are whole cm.
    return Math.round(parsed.cm) + 'x' + Math.round(parsed.cmH) + '-' + ori;
  }

  // Normalise a room_scene Size field value the same way, so admin entry
  // is forgiving: "50 x 70 Portrait", "50X70-portrait" → "50x70-portrait".
  // If no orientation is present, defaults to portrait.
  // Also accepts "50×70" (Unicode multiply) and similar.
  function normaliseSizeKey(raw) {
    if (!raw) return '';
    const s = String(raw)
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x');
    // A-size key, e.g. "a3-portrait" / "a3 landscape" / bare "a3".
    const am = s.match(/\ba([0-7])\b/);
    if (am) {
      const ori = s.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
      return 'a' + am[1] + '-' + ori;
    }
    const m = s.match(/(\d+)\s*x\s*(\d+)/);
    if (!m) return '';
    const ori = s.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
    return m[1] + 'x' + m[2] + '-' + ori;
  }

  /**
   * Shopify JSON may hand size_class as a string, number, or (rarely) list.
   * Coerce to a single string for normaliseSizeKey.
   */
  function coerceSceneSizeClass(raw) {
    if (raw == null || raw === '') return '';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'number' && !isNaN(raw)) return String(raw);
    if (Array.isArray(raw)) {
      for (let i = 0; i < raw.length; i++) {
        const c = coerceSceneSizeClass(raw[i]);
        if (c) return c;
      }
      return '';
    }
    if (typeof raw === 'object') {
      if (raw.handle != null) return String(raw.handle);
      if (raw.value != null) return coerceSceneSizeClass(raw.value);
    }
    return '';
  }

  /**
   * True when a library scene's normalised size refers to the same print as
   * `wantKey` (already normalised). Portrait: exact WxH + orientation.
   * Landscape: also allows swapped WxH (admin often enters 70×50 vs 50×70).
   */
  function dimsMatchForPrintCarousel(wantKey, sceneSizeNorm) {
    if (!wantKey || !sceneSizeNorm) return false;
    if (wantKey === sceneSizeNorm) return true;
    const mw = wantKey.match(/^(\d+)\s*x\s*(\d+)/);
    const ms = sceneSizeNorm.match(/^(\d+)\s*x\s*(\d+)/);
    if (!mw || !ms) return false;
    const w1 = parseInt(mw[1], 10);
    const h1 = parseInt(mw[2], 10);
    const w2 = parseInt(ms[1], 10);
    const h2 = parseInt(ms[2], 10);
    const o1 =
      wantKey.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
    const o2 =
      sceneSizeNorm.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
    if (o1 !== o2) return false;
    if (w1 === w2 && h1 === h2) return true;
    if (o1 === 'landscape' && w1 === h2 && h1 === w2) return true;
    return false;
  }

  // Convert cm dimensions to a printable display, locale-aware.
  function dimDisplay(parsed, country) {
    if (parsed.kind === 'asize') return parsed.name;
    if (parsed.kind !== 'std') return parsed.raw;
    if (country === 'us') {
      const wIn = Math.round(parsed.cm / 2.54 * 10) / 10;
      const hIn = Math.round(parsed.cmH / 2.54 * 10) / 10;
      return `${wIn}″ × ${hIn}″`;
    }
    return `${parsed.cm} × ${parsed.cmH} cm`;
  }

  function parseFabBoolean(value, defaultValue) {
    if (typeof value === 'boolean') return value;
    if (value == null || value === '') return defaultValue;

    const s = String(value).trim().toLowerCase();

    if (s === 'false' || s === '0' || s === 'no' || s === 'off') return false;
    if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;

    return defaultValue;
  }

  // ============================================================
  // Picker — one instance per .fab-size-picker root
  // ============================================================
  class FabSizePicker {
    constructor(root) {
      this.root = root;
      this.select = root.querySelector('.fab-size__select-shim');
      this.toggle = root.querySelector('[data-fab-preview-toggle]');
      this.roomWrap = root.querySelector('[data-fab-room-wrap]');
      this.sceneLayer = root.querySelector('[data-fab-scene-layer]');
      this.print = root.querySelector('[data-fab-print]');
      this.printImg = this.print && this.print.querySelector('[data-fab-print-img]');
      if (this.print && !this.printImg) {
        const img = document.createElement('img');
        img.className = 'fab-size__print-img';
        img.setAttribute('data-fab-print-img', '');
        img.alt = '';
        img.decoding = 'async';
        img.loading = 'eager';
        this.print.appendChild(img);
        this.printImg = img;
      }
      // Readout pill (the "70 × 100 cm" dimension label) is intentionally
      // disabled — the room mock shows the print at honest scale, the
      // floating label is redundant clutter. Forced null so every
      // `if (this.readout)` guard below skips it. To bring it back, change
      // this to: root.querySelector('[data-fab-readout]').
      this.readout = null;
      // Caption ("Full wall scene · wall 3.6m wide") is disabled — the
      // measurement arrows now show the wall dimensions far more clearly.
      // Forced null so every `if (this.caption…)` guard skips it.
      this.captionScene = null;
      this.captionScale = null;
      this.adviceVerdict = root.querySelector('[data-fab-advice-verdict]');
      this.adviceCounter = root.querySelector('[data-fab-advice-counter]');
      this.adviceVariant = pickAdviceVariant();
      this.cells = Array.from(root.querySelectorAll('[data-fab-size-cell]'));
      this.asizeToggle = root.querySelector('[data-fab-asize-toggle]');
      this.asizeBlock = root.querySelector('[data-fab-asize-block]');
      this.variantSelectsEl = root.closest('variant-selects');

      this.country = (root.dataset.fabCountry || '').toLowerCase();
      this.locale = (root.dataset.fabLocale || '').toLowerCase();
      // The product's artwork orientation, from custom.orientation, stamped
      // on the root by the Liquid snippet. Portrait unless explicitly
      // landscape. Used to pick the right room_scene (portrait vs landscape
      // are separate entries).
      this.orientation =
        (root.dataset.fabOrientation || '').toLowerCase() === 'landscape'
          ? 'landscape'
          : 'portrait';
      // Full-bleed: custom.fullbleed boolean, stamped on the root. Default
      // true (image goes edge to edge). When the attribute is exactly
      // 'false', the print has a white paper border.
      this.fullBleed = parseFabBoolean(root.dataset.fabFullbleed, true);
      // Canvas products render with a gallery-wrap + floater-frame model
      // instead of the flat paper moulding. The Liquid snippet stamps
      // product.type on the root as data-fab-product-type.
      this.isCanvas =
        (root.dataset.fabProductType || '').toLowerCase().indexOf('canvas') !== -1;
      console.log('[Fab size picker fullBleed]', {
        raw: root.dataset.fabFullbleed,
        parsed: this.fullBleed,
        isCanvas: this.isCanvas,
        productType: root.dataset.fabProductType,
      });

      this.expanded = false;
      // True after the customer taps "Hide room"; size pills won't reopen until "See in room".
      this.roomDismissedByUser = false;
      this.asizeExpanded = false;
      this.currentScene = null;
      this.currentParsed = null;

      // Room library + this product's recommended rooms.
      this.roomLibrary = readRoomLibrary(root);
      this.productRooms = readProductRooms(root);
      this.currentRoomScene = null;

      // Multiple room_scene rows for the same print size → arrows + dots + swipe.
      // `_roomCarouselBuiltSig` tracks whether controls match the scene list.
      this._roomCarouselUi = null;
      this._roomCarouselScenes = null;
      this._roomCarouselIndex = 0;
      this._roomCarouselBuiltSig = '';
      this._carouselPtrStartX = null;
      this._onCarouselPtrDown = null;
      this._onCarouselPtrUp = null;

      // Canvas compositor. Created lazily on first room expand so we don't
      // pay for a canvas on PDPs where the room mock is never opened.
      // compositorActive is true only while a real composite is showing.
      this.compositor = null;
      this.compositorActive = false;

      this.init();
    }

    applyFullBleedMode() {
      // For now, fix art prints only. Canvas handling can come later.
      const nonFullBleedArtPrint = !this.isCanvas && this.fullBleed === false;

      this.root.classList.toggle(
        'fab-size-picker--non-full-bleed',
        nonFullBleedArtPrint
      );

      if (this.print) {
        this.print.classList.toggle(
          'fab-size__print--non-full-bleed',
          nonFullBleedArtPrint
        );
      }

      if (this.printImg) {
        this.printImg.classList.toggle(
          'fab-size__print-img--non-full-bleed',
          nonFullBleedArtPrint
        );
      }
    }

    init() {
      // Decorate every cell with parsed metadata and the scene it belongs
      // to (used for the CSS placeholder fallback room).
      this.cells.forEach(cell => {
        const raw = cell.dataset.fabSizeValue;
        const parsed = parseSizeValue(raw);
        cell.__fabParsed = parsed;
        if (parsed && parsed.cm != null) {
          cell.dataset.fabScene = sceneForCm(parsed.cm);
        }
        cell.addEventListener('click', (e) => this.onCellClick(cell, e));
      });

 // Preview toggle
if (this.toggle && this.roomWrap) {
  this.toggle.addEventListener('click', (e) => {
    e.preventDefault();
    this.onToggleClick();
  });
}

// A-sizes toggle
if (this.asizeToggle && this.asizeBlock) {
  this.asizeToggle.addEventListener('click', (e) => {
    e.preventDefault();
    this.toggleAsizes();
  });
}

      this.applyFullBleedMode();

      // Initial: paint the room mock for the currently-selected size
      // (so when the user expands later, it's pre-correct)
      const initialCell = this.cells.find(c => c.classList.contains('fab-size__cell--selected') || c.classList.contains('fab-size__asize-cell--selected')) || this.cells[0];
      if (initialCell && initialCell.__fabParsed) {
        this.applySize(initialCell.__fabParsed, /*animate*/ false);
      }
      if (initialCell) this.updateAdvice(initialCell);

      // If the variant-selects fires a change for another option (e.g. frame),
      // we don't need to do anything — the underlying select still holds the
      // right size value. But if something external sets select.value, mirror.
      if (this.select) {
        this.select.addEventListener('change', () => this.syncFromSelect());
      }
      // Reposition the readout if the stage resizes (e.g. after expand, on
      // viewport change). Without this, the readout would sit at the wrong
      // pixel offset because it was computed against the old stage height.
      //
      // Debounced: the expand animation resizes the stage every frame for
      // ~460ms. Without debouncing that fires applySize() — and a fresh
      // compositor render — dozens of times. We coalesce to one call once
      // the size settles.
      if (typeof ResizeObserver !== 'undefined') {
        const stage = this.print && this.print.parentElement;
        if (stage) {
          this._resizeObserver = new ResizeObserver(() => {
            window.clearTimeout(this._resizeTimer);
            this._resizeTimer = window.setTimeout(() => {
              if (this.currentParsed) {
                // animate=false; we don't want a transition on resize.
                this.applySize(this.currentParsed, false);
              }
            }, 120);
          });
          this._resizeObserver.observe(stage);
        }
      }

      if (this.variantSelectsEl) {
        const schedulePillPrices = () => {
          window.clearTimeout(this._pillPriceTimer);
          this._pillPriceTimer = window.setTimeout(() => this.updatePillPrices(), 50);
        };
        this.variantSelectsEl.addEventListener('change', schedulePillPrices);

        this.variantSelectsEl.addEventListener('variant:change', e => {
          this.syncPrintArt(e.detail);
          schedulePillPrices();
          const value = this.select && this.select.value;
          const cell = value && this.cells.find(c => c.dataset.fabSizeValue === value);
          if (cell && cell.__fabParsed) {
            this.setSelectedCell(cell);
            this.applySize(cell.__fabParsed, false);
          } else if (this.currentParsed) {
            this.applySize(this.currentParsed, false);
          }
          this._syncAsizePanelToSelection();
        });
      }

      this.updatePillPrices();
      this._syncAsizePanelToSelection();

      // If there is no usable room library, the compositor will fall back
      // to the CSS placeholder scenes. Nothing else to do at init.
      if (!this.roomLibrary.length) {
        // No real rooms yet — placeholder scenes remain in use.
      }
    }

    syncFromSelect() {
      const value = this.select.value;
      const cell = this.cells.find(c => c.dataset.fabSizeValue === value);
      if (cell && cell.__fabParsed) {
        this.setSelectedCell(cell);
        this.applySize(cell.__fabParsed, true);
      }
      this._syncAsizePanelToSelection();
    }

    setSelectedCell(targetCell) {
      this.cells.forEach(c => {
        const isAsizeCell = c.classList.contains('fab-size__asize-cell');
        const selected = c === targetCell;
        if (isAsizeCell) {
          c.classList.toggle('fab-size__asize-cell--selected', selected);
        } else {
          c.classList.toggle('fab-size__cell--selected', selected);
        }
        c.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      this.updateAdvice(targetCell);
    }

    updateAdvice(cell) {
      const key = adviceKeyForCell(cell);
      const copy =
        key && SIZE_ADVICE[key] ? SIZE_ADVICE[key][this.adviceVariant] : null;
      if (this.adviceVerdict) {
        this.adviceVerdict.textContent = copy ? copy.line1 : '';
      }
      if (this.adviceCounter) {
        this.adviceCounter.textContent = copy ? copy.line2 : '';
      }
    }

    onCellClick(cell) {
      const value = cell.dataset.fabSizeValue;
      const parsed = cell.__fabParsed;
      if (!value || !parsed) return;

      // Update the hidden select FIRST, dispatch change to wake the
      // variant pipeline. product-form.js will fire variant:change,
      // frame-price-updater.js will recompute frame deltas, etc.
      if (this.select.value !== value) {
        this.select.value = value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.setSelectedCell(cell);
      this.applySize(parsed, true);

      const isCollapsedMode =
        (this.root.dataset.fabSizeMode || '') === 'collapsed-tabbed';
      if (!isCollapsedMode && !this.expanded && !this.roomDismissedByUser) {
        this.expand();
      }
    }

    onToggleClick() {
      if (this.expanded) {
        this.roomDismissedByUser = true;
        this.collapse();
      } else {
        this.roomDismissedByUser = false;
        this.expand();
      }
    }

    /**
     * Given a size+orientation key (e.g. "50x70-portrait"), returns the
     * best room scene for THIS product, or null if the library can't
     * serve one (caller then uses the CSS placeholder scene).
     *
     * One room_scene exists per size per orientation. Matching is exact;
     * the fallback degrades by nearest size BUT NEVER crosses orientation
     * — a portrait print never falls back to a landscape room.
     */
    pickRoomScene(sizeKeyWanted) {
      const lib = this.roomLibrary;
      if (!lib || !lib.length) return null;

      const want = normaliseSizeKey(sizeKeyWanted);
      if (!want) return null;
      const wantOri = want.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
      const rooms = this.productRooms || [];

      // Stable index for deterministic tie-breaking. The product id is
      // stamped on the picker root by the Liquid serialiser.
      const pid = parseInt(this.root.dataset.fabProductId, 10) || 0;
      const pickStable = function (candidates) {
        if (!candidates.length) return null;
        return candidates[pid % candidates.length];
      };

      // Orientation guard — used by every fallback so we never hand a
      // landscape room to a portrait print or vice versa.
      const sameOri = function (s) {
        return (s.size.indexOf('landscape') !== -1 ? 'landscape' : 'portrait')
               === wantOri;
      };

      // "Nearest size" fallback — by total cm area, same orientation only.
      const keyArea = function (k) {
        const m = String(k).match(/(\d+)x(\d+)/);
        return m ? parseInt(m[1], 10) * parseInt(m[2], 10) : 0;
      };
      const wantArea = keyArea(want);
      const nearestBySize = function (candidates) {
        const pool = candidates.filter(sameOri);
        if (!pool.length) return null;
        if (!wantArea) return pickStable(pool);
        let best = null, bestDist = Infinity;
        pool.forEach(function (s) {
          const d = Math.abs(keyArea(s.size) - wantArea);
          if (d < bestDist) { bestDist = d; best = s; }
        });
        return best;
      };

      // 1. Exact room + exact size, product rooms in declared order.
      for (let i = 0; i < rooms.length; i++) {
        const exact = lib.filter(function (s) {
          return s.roomType === rooms[i] && s.size === want;
        });
        if (exact.length) return pickStable(exact);
      }

      // 2. Exact size, any room.
      const sizeOnly = lib.filter(function (s) { return s.size === want; });
      if (sizeOnly.length) return pickStable(sizeOnly);

      // 3. Preferred room, nearest size (same orientation).
      for (let j = 0; j < rooms.length; j++) {
        const roomOnly = lib.filter(function (s) {
          return s.roomType === rooms[j];
        });
        if (roomOnly.length) {
          const hit = nearestBySize(roomOnly);
          if (hit) return hit;
        }
      }

      // 4. Nearest size, any room (same orientation).
      return nearestBySize(lib);
    }

    /**
     * All library scenes whose normalised size key matches `sizeKeyWanted`
     * exactly (same orientation). Used to decide whether to show a carousel.
     */
    collectExactSizeScenes(sizeKeyWanted) {
      const lib = this.roomLibrary;
      if (!lib || !lib.length) return [];
      const want = normaliseSizeKey(sizeKeyWanted);
      if (!want) return [];
      const wantOri =
        want.indexOf('landscape') !== -1 ? 'landscape' : 'portrait';
      const sameOri = function (s) {
        return (
          (s.size.indexOf('landscape') !== -1 ? 'landscape' : 'portrait') ===
          wantOri
        );
      };
      const pool = lib.filter(function (s) {
        return sameOri(s) && dimsMatchForPrintCarousel(want, s.size);
      });
      if (pool.length <= 1) return pool;
      const seen = {};
      const out = [];
      for (let i = 0; i < pool.length; i++) {
        const s = pool[i];
        const k = (s.id ? String(s.id) : '') + '|' + String(s.image || '');
        if (seen[k]) continue;
        seen[k] = true;
        out.push(s);
      }
      return out.length > 1 ? out : pool.slice(0, 1);
    }

    /**
     * Order scenes for carousel: product `custom.room` order first (the
     * first listed room that appears in the pool wins as slide 0), then
     * any other rooms in stable library order.
     */
    orderScenesForCarousel(pool) {
      if (!pool || pool.length <= 1) return pool ? pool.slice() : [];
      const rooms = this.productRooms || [];
      const decorated = pool.map((s, i) => {
        let rank = 999;
        const rt = s.roomType || '';
        for (let r = 0; r < rooms.length; r++) {
          if (rt === rooms[r]) {
            rank = r;
            break;
          }
        }
        return { s, rank, i };
      });
      decorated.sort(function (a, b) {
        return a.rank - b.rank || a.i - b.i;
      });
      return decorated.map(function (d) {
        return d.s;
      });
    }

    _carouselSigFromScenes(scenes) {
      if (!scenes || !scenes.length) return '';
      return scenes
        .map(function (s) {
          return (s.id || '') + '|' + (s.image || '');
        })
        .join(';;');
    }

    _destroyRoomCarousel() {
      const stage = this.print && this.print.parentElement;
      if (stage && this._onCarouselPtrDown && this._onCarouselPtrUp) {
        stage.removeEventListener('pointerdown', this._onCarouselPtrDown);
        stage.removeEventListener('pointerup', this._onCarouselPtrUp);
        stage.removeEventListener('pointercancel', this._onCarouselPtrUp);
      }
      this._onCarouselPtrDown = null;
      this._onCarouselPtrUp = null;
      this._carouselPtrStartX = null;
      if (this._roomCarouselUi && this._roomCarouselUi.parentNode) {
        this._roomCarouselUi.parentNode.removeChild(this._roomCarouselUi);
      }
      this._roomCarouselUi = null;
      this._roomCarouselBuiltSig = '';
    }

    _roomCarouselDotAriaLabel(i, n) {
      const tpl =
        this.root.getAttribute('data-fab-room-dot-aria') ||
        'Room view {current} of {total}';
      return tpl.replace('{current}', String(i + 1)).replace('{total}', String(n));
    }

    _roomCarouselArrowSvgPrev() {
      return (
        '<svg viewBox="0 0 512 512" aria-hidden="true">' +
        '<path fill="none" stroke="currentColor" stroke-linecap="round" ' +
        'stroke-linejoin="round" stroke-width="48" d="M328 112L184 256l144 144"/>' +
        '</svg>'
      );
    }

    _roomCarouselArrowSvgNext() {
      return (
        '<svg viewBox="0 0 512 512" aria-hidden="true">' +
        '<path fill="none" stroke="currentColor" stroke-linecap="round" ' +
        'stroke-linejoin="round" stroke-width="48" d="M184 112l144 144-144 144"/>' +
        '</svg>'
      );
    }

    _syncRoomCarouselChrome() {
      const n = this._roomCarouselScenes
        ? this._roomCarouselScenes.length
        : 0;
      const idx = Math.max(
        0,
        Math.min(this._roomCarouselIndex, Math.max(0, n - 1))
      );
      this._roomCarouselIndex = idx;
      if (!this._roomCarouselUi) return;
      const prevBtn = this._roomCarouselUi.querySelector(
        '[data-fab-room-carousel-prev]'
      );
      const nextBtn = this._roomCarouselUi.querySelector(
        '[data-fab-room-carousel-next]'
      );
      if (prevBtn) {
        const hidePrev = idx === 0 || n <= 1;
        prevBtn.toggleAttribute('hidden', hidePrev);
        prevBtn.setAttribute('aria-hidden', hidePrev ? 'true' : 'false');
      }
      if (nextBtn) {
        const hideNext = n <= 1 || idx >= n - 1;
        nextBtn.toggleAttribute('hidden', hideNext);
        nextBtn.setAttribute('aria-hidden', hideNext ? 'true' : 'false');
      }
      const buttons = this._roomCarouselUi.querySelectorAll(
        '.fab-size__room-carousel-dot'
      );
      buttons.forEach((btn, i) => {
        const active = i === idx;
        btn.classList.toggle('fab-size__room-carousel-dot--active', active);
        btn.setAttribute('aria-current', active ? 'true' : 'false');
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.setAttribute('aria-label', this._roomCarouselDotAriaLabel(i, n));
      });
    }

    _setRoomCarouselIndex(stage, nextIdx) {
      if (!this._roomCarouselScenes || !this._roomCarouselScenes.length) return;
      const n = this._roomCarouselScenes.length;
      const i = Math.max(0, Math.min(nextIdx, n - 1));
      if (i === this._roomCarouselIndex) return;
      this._roomCarouselIndex = i;
      this._syncRoomCarouselChrome();
      const scene = this._roomCarouselScenes[i];
      this.currentRoomScene = scene;
      this._compositorRenderPromise(scene, stage);
    }

    _ensureRoomCarousel(stage, scenes) {
      const self = this;
      if (!scenes || scenes.length <= 1) {
        this._destroyRoomCarousel();
        return;
      }
      const sig = this._carouselSigFromScenes(scenes);
      if (this._roomCarouselUi && this._roomCarouselBuiltSig === sig) {
        this._syncRoomCarouselChrome();
        return;
      }
      this._destroyRoomCarousel();

      const ui = document.createElement('div');
      ui.className = 'fab-size__room-carousel-ui';

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'fab-gallery__arrow fab-gallery__arrow--prev';
      prevBtn.setAttribute('data-fab-room-carousel-control', '');
      prevBtn.setAttribute('data-fab-room-carousel-prev', '');
      prevBtn.setAttribute(
        'aria-label',
        this.root.getAttribute('data-fab-prev-slide-label') || 'Previous'
      );
      prevBtn.innerHTML = this._roomCarouselArrowSvgPrev();
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self._setRoomCarouselIndex(stage, self._roomCarouselIndex - 1);
      });

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'fab-gallery__arrow fab-gallery__arrow--next';
      nextBtn.setAttribute('data-fab-room-carousel-control', '');
      nextBtn.setAttribute('data-fab-room-carousel-next', '');
      nextBtn.setAttribute(
        'aria-label',
        this.root.getAttribute('data-fab-next-slide-label') || 'Next'
      );
      nextBtn.innerHTML = this._roomCarouselArrowSvgNext();
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self._setRoomCarouselIndex(stage, self._roomCarouselIndex + 1);
      });

      const dotsWrap = document.createElement('div');
      dotsWrap.className = 'fab-size__room-carousel-dots';
      dotsWrap.setAttribute('role', 'tablist');
      dotsWrap.setAttribute(
        'aria-label',
        this.root.getAttribute('data-fab-room-dots-label') || 'Room photos'
      );

      const n = scenes.length;
      for (let i = 0; i < n; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fab-size__room-carousel-dot';
        btn.setAttribute('data-fab-room-carousel-control', '');
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', 'false');
        btn.setAttribute('aria-label', this._roomCarouselDotAriaLabel(i, n));
        const sceneIndex = i;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          self._setRoomCarouselIndex(stage, sceneIndex);
        });
        dotsWrap.appendChild(btn);
      }

      ui.appendChild(prevBtn);
      ui.appendChild(nextBtn);
      ui.appendChild(dotsWrap);
      stage.appendChild(ui);
      this._roomCarouselUi = ui;
      this._roomCarouselBuiltSig = sig;
      this._syncRoomCarouselChrome();

      this._onCarouselPtrDown = function (e) {
        if (!self._roomCarouselScenes || self._roomCarouselScenes.length <= 1) {
          return;
        }
        if (
          e.target.closest &&
          e.target.closest('[data-fab-room-carousel-control]')
        ) {
          return;
        }
        if (typeof e.button === 'number' && e.button !== 0) return;
        self._carouselPtrStartX = e.clientX;
      };
      this._onCarouselPtrUp = function (e) {
        if (self._carouselPtrStartX == null) return;
        const start = self._carouselPtrStartX;
        self._carouselPtrStartX = null;
        if (!self._roomCarouselScenes || self._roomCarouselScenes.length <= 1) {
          return;
        }
        const dx = e.clientX - start;
        if (dx > 48) {
          self._setRoomCarouselIndex(stage, self._roomCarouselIndex - 1);
        } else if (dx < -48) {
          self._setRoomCarouselIndex(stage, self._roomCarouselIndex + 1);
        }
      };
      stage.addEventListener('pointerdown', this._onCarouselPtrDown);
      stage.addEventListener('pointerup', this._onCarouselPtrUp);
      stage.addEventListener('pointercancel', this._onCarouselPtrUp);
    }

    _compositorRenderPromise(scene, stage) {
      if (!scene) {
        return Promise.resolve(false);
      }
      if (!this.compositor) {
        this.compositor = new window.FabRoomCompositor(stage);
      }
      return this.compositor
        .render({
          scene: scene,
          artUrl: this._unframedArtUrl(),
          frameColor: this.getSelectedFrameColor(),
          country: this.country,
          fullBleed: this.fullBleed,
          isCanvas: this.isCanvas,
        })
        .then(result => {
          if (result === 'stale') return result;
          if (result) {
            this.compositorActive = true;
            this.compositor.show();
            if (this.sceneLayer) this.sceneLayer.style.opacity = '0';
            if (this.print) this.print.style.opacity = '0';
            this._positionReadoutFromCompositor();
          } else {
            this._useCssScene();
          }
          return result;
        })
        .catch(() => {
          this._useCssScene();
          return false;
        });
    }

    getVariantsArray() {
      if (!this.variantSelectsEl) return [];
      try {
        return JSON.parse(this.variantSelectsEl.dataset.variants || '[]');
      } catch (_) {
        return [];
      }
    }

    getSelectedOptionValues() {
      if (!this.variantSelectsEl) return [];
      const controls = Array.from(
        this.variantSelectsEl.querySelectorAll(
          'select[data-option-position], input[type="radio"][data-option-position]:checked'
        )
      );
      controls.sort((a, b) => {
        const pa = parseInt(a.getAttribute('data-option-position'), 10) || 0;
        const pb = parseInt(b.getAttribute('data-option-position'), 10) || 0;
        return pa - pb;
      });
      return controls.map(el => el.value);
    }

    findMatchingVariant() {
      const variants = this.getVariantsArray();
      const selectedOptions = this.getSelectedOptionValues();
      if (!variants.length || !selectedOptions.length) return null;
      return (
        variants.find(variant => {
          const variantOptions = (variant.options || []).filter(
            o => o !== null && o !== undefined && o !== ''
          );
          return selectedOptions.every((sel, index) => sel === variantOptions[index]);
        }) || null
      );
    }

    /**
     * Variant for a hypothetical size value while keeping every other
     * option (frame, etc.) at the customer's current selection.
     */
    findVariantForSizeValue(sizeValue) {
      if (!this.select || sizeValue == null || sizeValue === '') return null;
      const variants = this.getVariantsArray();
      const selected = this.getSelectedOptionValues();
      if (!variants.length || !selected.length) return null;
      const sizePos = parseInt(this.select.getAttribute('data-option-position'), 10);
      if (!(sizePos >= 1)) return null;
      const sizeIdx = sizePos - 1;
      if (sizeIdx < 0 || sizeIdx >= selected.length) return null;
      const target = selected.map((v, i) => (i === sizeIdx ? String(sizeValue) : v));
      return (
        variants.find(variant => {
          const vo = (variant.options || []).filter(
            o => o !== null && o !== undefined && o !== ''
          );
          if (vo.length !== target.length) return false;
          return target.every((t, i) => vo[i] === t);
        }) || null
      );
    }

    /** Refresh .fab-size__price / .fab-size__asize-price when frame (or other options) change. */
    updatePillPrices() {
      if (!this.select || !this.variantSelectsEl) return;
      const selected = this.getSelectedOptionValues();
      if (!selected.length) return;
      this.cells.forEach(cell => {
        const sizeVal = cell.dataset.fabSizeValue;
        if (!sizeVal) return;
        const priceEl = cell.querySelector('.fab-size__price, .fab-size__asize-price');
        if (!priceEl) return;
        const v = this.findVariantForSizeValue(sizeVal);
        if (v && v.price != null) {
          priceEl.textContent = formatMoneyCents(v.price);
        }
      });

      const collapsed = this.root.__fabSizePickerCollapsed;
      if (collapsed && typeof collapsed.syncSummaryFromSelect === 'function') {
        collapsed.syncSummaryFromSelect();
      }
    }

    /** Shopify often returns protocol-relative URLs; normalize for CSS + fetch. */
    normalizeImageUrl(raw) {
      if (!raw || typeof raw !== 'string') return '';
      const s = raw.trim();
      if (s.indexOf('//') === 0) return 'https:' + s;
      return s;
    }

    variantFeaturedImageUrl(variant) {
      if (!variant) return '';
      if (typeof variant.featured_image === 'string' && variant.featured_image) {
        return this.normalizeImageUrl(variant.featured_image);
      }
      if (variant.featured_image && typeof variant.featured_image.src === 'string') {
        return this.normalizeImageUrl(variant.featured_image.src);
      }
      const fm = variant.featured_media && variant.featured_media.preview_image;
      if (fm && fm.src) {
        return this.normalizeImageUrl(fm.src);
      }
      return '';
    }

    syncPrintArt(variantHint) {
      if (!this.print || !this.printImg) return;
      let url = '';
      if (variantHint) {
        url = this.variantFeaturedImageUrl(variantHint);
      }
      if (!url) {
        url = this.variantFeaturedImageUrl(this.findMatchingVariant());
      }
      if (!url && this.variantSelectsEl) {
        try {
          const sel = this.variantSelectsEl.querySelector('[data-selected-variant]');
          if (sel && sel.textContent) {
            url = this.variantFeaturedImageUrl(JSON.parse(sel.textContent));
          }
        } catch (_) {}
      }
      this.print.style.backgroundImage = 'none';
      this.printImg.src = url || FALLBACK_PRINT_DATA_URI;
      this.printImg.alt = '';
    }

    // ----------------------------------------------------------
    // Compositor wiring
    // ----------------------------------------------------------

    // Read the live frame swatch and canonicalise to a lowercase colour
    // key ('black'|'white'|'oak'|'brown'|'silver'|'gold'|'unframed').
    // The frame picker uses radio inputs with class "fab-frame-radio"
    // (see product-variant-picker-new.liquid).
    getSelectedFrameColor() {
      const vs = this.variantSelectsEl;
      if (!vs) return 'unframed';
      const checked =
        vs.querySelector('input.fab-frame-radio:checked') ||
        vs.querySelector('fieldset.product-form__input--swatch input[type="radio"]:checked');
      let raw = checked ? checked.value : '';
      if (!raw) {
        try {
          const sel = vs.querySelector('[data-selected-variant]');
          if (sel) {
            const v = JSON.parse(sel.textContent || '{}');
            const opts = (v.options || []).map(o => String(o).toLowerCase().trim());
            const FRAMES = ['black', 'white', 'oak', 'brown', 'silver', 'gold'];
            raw = opts.find(o => FRAMES.indexOf(o) !== -1) || '';
          }
        } catch (e) {}
      }
      const c = String(raw).toLowerCase().trim();
      const MAP = {
        'unframed': 'unframed', 'none': 'unframed', 'sans': 'unframed', 'sans cadre': 'unframed',
        'non encadré': 'unframed', 'ohne': 'unframed', 'ohne rahmen': 'unframed',
        'senza': 'unframed', 'senza cornice': 'unframed', 'zonder': 'unframed',
        'zonder lijst': 'unframed', 'utan': 'unframed', 'utan ram': 'unframed',
        'sin': 'unframed', 'sin marco': 'unframed',
        'black': 'black', 'noir': 'black', 'schwarz': 'black', 'nero': 'black',
        'zwart': 'black', 'svart': 'black', 'negro': 'black',
        'white': 'white', 'blanc': 'white', 'weiß': 'white', 'weiss': 'white',
        'bianco': 'white', 'wit': 'white', 'vit': 'white', 'blanco': 'white',
        'oak': 'oak', 'chêne': 'oak', 'roble': 'oak', 'eiche': 'oak',
        'rovere': 'oak', 'eiken': 'oak', 'eik': 'oak', 'ek': 'oak',
        'brown': 'brown', 'marron': 'brown', 'marrón': 'brown', 'marrone': 'brown',
        'braun': 'brown', 'bruin': 'brown', 'brun': 'brown', 'walnut': 'brown',
        'silver': 'silver', 'argent': 'silver', 'argenté': 'silver', 'silber': 'silver',
        'argento': 'silver', 'zilver': 'silver', 'plata': 'silver', 'plateado': 'silver',
        'gold': 'gold', 'or': 'gold', 'doré': 'gold', 'oro': 'gold',
        'goud': 'gold', 'guld': 'gold', 'dorado': 'gold'
      };
      return MAP[c] || 'unframed';
    }

    // The bare, unframed artwork URL for the compositor to frame live.
    // It must NOT be a pre-framed mock.
    //
    // On this catalogue, variants carry NO images (variant.image is null
    // for every variant), so variant lookups all come back empty. The
    // bare artwork is the PRODUCT's featured image (first media item) —
    // the clean painting, not the framed `il_fullxfull` mocks. The Liquid
    // snippet stamps that URL onto the root as data-fab-art-url.
    //
    // Order: data-fab-art-url → an unframed variant image (future-proof,
    // if variants ever get images) → matching variant → any variant.
    _unframedArtUrl() {
      // 1. The product featured image, stamped by the Liquid snippet.
      const stamped = this.root.dataset.fabArtUrl;
      if (stamped) {
        const u = this.normalizeImageUrl(stamped);
        if (u) return u;
      }

      // 2. An unframed variant's own image (only if variants have images).
      const variants = this.getVariantsArray();
      const UNFRAMED = ['unframed', 'none', 'sans', 'ohne', 'senza', 'zonder', 'utan', 'sin'];
      for (let i = 0; i < variants.length; i++) {
        const opts = (variants[i].options || []).map(o => String(o).toLowerCase().trim());
        if (opts.some(o => UNFRAMED.indexOf(o) !== -1)) {
          const u = this.variantFeaturedImageUrl(variants[i]);
          if (u) return u;
        }
      }

      // 3. The matching variant, then any variant.
      const m = this.variantFeaturedImageUrl(this.findMatchingVariant());
      if (m) return m;
      for (let j = 0; j < variants.length; j++) {
        const u = this.variantFeaturedImageUrl(variants[j]);
        if (u) return u;
      }
      return '';
    }

    // Drive the canvas compositor. Decides between a real composited room
    // and the CSS placeholder scene. Safe to call whether or not the room
    // mock is currently expanded. `sizeKeyWanted` is the normalised size
    // key for the current selection (e.g. "50x70-portrait"), or '' for A-sizes.
    //
    // When several room_scene metaobjects share that exact size, all of
    // them are offered as a carousel (recommended `custom.room` order
    // first); otherwise behaviour matches pickRoomScene() fallbacks.
    renderRoom(parsed, sizeKeyWanted) {
      if (!parsed) return;
      const stage = this.print && this.print.parentElement;
      if (!stage) return;

      // No compositor, no library, or an A-size (no room scenes exist for
      // A-sizes) → CSS placeholder scene.
      if (typeof window.FabRoomCompositor === 'undefined' ||
          !this.roomLibrary.length ||
          !sizeKeyWanted) {
        this._useCssScene();
        return;
      }

      const exact = this.collectExactSizeScenes(sizeKeyWanted);
      let scene = null;
      let carouselScenes = null;

      if (exact.length > 1) {
        carouselScenes = this.orderScenesForCarousel(exact);
        const sig = this._carouselSigFromScenes(carouselScenes);
        if (this._roomCarouselBuiltSig !== sig) {
          this._roomCarouselIndex = 0;
        }
        this._roomCarouselScenes = carouselScenes;
        this._roomCarouselIndex = Math.min(
          this._roomCarouselIndex,
          carouselScenes.length - 1
        );
        this._ensureRoomCarousel(stage, carouselScenes);
        scene = carouselScenes[this._roomCarouselIndex];
      } else {
        this._destroyRoomCarousel();
        this._roomCarouselScenes = null;
        if (exact.length === 1) {
          scene = exact[0];
        } else {
          scene = this.pickRoomScene(sizeKeyWanted);
        }
      }

      if (!scene) {
        this._useCssScene();
        return;
      }

      this.currentRoomScene = scene;
      this._compositorRenderPromise(scene, stage);
    }

    // Fall back to the CSS placeholder scene: clear the compositor canvas
    // and restore the scene layer + <img> print to visible.
    _useCssScene() {
      this._destroyRoomCarousel();
      this._roomCarouselScenes = null;
      this.compositorActive = false;
      if (this.compositor) this.compositor.clear();
      if (this.sceneLayer) this.sceneLayer.style.opacity = '';
      if (this.print) this.print.style.opacity = '';
    }

    // Position the readout pill above the compositor's drawn piece. The
    // piece rect is in CSS px relative to the stage; the readout is a
    // stage child, so the coords are used directly.
    _positionReadoutFromCompositor() {
      if (!this.readout || !this.compositor) return;
      const rect = this.compositor.getPieceRect();
      if (!rect) return;
      this.readout.style.left = (rect.x + rect.w / 2) + 'px';
      this.readout.style.transform = 'translateX(-50%)';
      this.readout.style.top = Math.max(4, rect.y - 28) + 'px';
    }

    // ----------------------------------------------------------
    // Applying a size to the room mock
    // ----------------------------------------------------------
    applySize(parsed, animate) {
      if (!parsed) return;

      const cm = parsed.cm || 40;
      const cmH =
        parsed.cmH != null
          ? parsed.cmH
          : parsed.kind === 'asize' && parsed.ratio
            ? cm * parsed.ratio
            : cm * 1.25;
      const targetScene = sceneForCm(cm);
      const wallCm = WALL_CM_BY_SCENE[targetScene] || 280;
      const widthFrac = Math.min(cm / wallCm, 0.92);
      const widthPct = widthFrac * 100;

      if (this.print) {
        this.print.style.setProperty('--fab-print-width-pct', widthPct.toFixed(2) + '%');
        this.print.style.aspectRatio = `${cm} / ${cmH}`;
        this.print.style.setProperty('--fab-print-baseline', BASELINE_PCT_BY_SCENE[targetScene]);
      }
      this.syncPrintArt();

      // Readout positioning. When the compositor is active it owns the
      // readout position (see _positionReadoutFromCompositor) — the CSS
      // scene geometry below would fight it, so skip it in that case.
      if (this.readout) {
        this.readout.textContent = dimDisplay(parsed, this.country);
        if (!this.compositorActive) {
          const stage = this.print && this.print.parentElement;
          if (stage) {
            const stageH = stage.clientHeight;
            if (stageH > 0) {
              const baseWpx = stage.clientWidth * widthFrac;
              const baseHpx = baseWpx * (cmH / cm);
              const printHpx = baseHpx;
              const printBottomPx = stageH * (BASELINE_PCT_BY_SCENE[targetScene] / 100);
              const printTopPx = printBottomPx - printHpx;
              this.readout.style.top = Math.max(4, printTopPx - 28) + 'px';
            }
          }
        }
      }

      // Scene swap if needed
      if (this.sceneLayer) {
        if (targetScene !== this.currentScene) {
          this.swapScene(targetScene, animate);
        } else if (this.currentScene == null) {
          this.sceneLayer.innerHTML = sceneHTML(targetScene);
          this.currentScene = targetScene;
          this.updateCaption(targetScene);
        } else {
          this.updateCaption(targetScene);
        }
      }

      // Drive the canvas compositor (falls back to the CSS scene
      // internally). Matched on the EXACT size+orientation key. Several
      // room_scene rows for the same key show a carousel; A-sizes yield ''
      // → CSS fallback.
      this.renderRoom(parsed, sizeKey(parsed, this.orientation));

      this.currentParsed = parsed;
    }

    swapScene(nextScene, animate) {
      if (!this.sceneLayer) return;
      if (!animate) {
        this.sceneLayer.innerHTML = sceneHTML(nextScene);
        this.currentScene = nextScene;
        this.updateCaption(nextScene);
        return;
      }
      // Fade out → swap → fade in
      this.sceneLayer.classList.add('is-fading');
      // Wait for the fade transition (about 280ms in CSS)
      const onFadeOut = () => {
        this.sceneLayer.removeEventListener('transitionend', onFadeOut);
        this.sceneLayer.innerHTML = sceneHTML(nextScene);
        this.currentScene = nextScene;
        this.updateCaption(nextScene);
        // Next frame, fade back in
        requestAnimationFrame(() => {
          this.sceneLayer.classList.remove('is-fading');
        });
      };
      this.sceneLayer.addEventListener('transitionend', onFadeOut, { once: true });
      // Safety: if for any reason transitionend doesn't fire, swap anyway.
      setTimeout(() => {
        if (this.sceneLayer.classList.contains('is-fading')) {
          onFadeOut();
        }
      }, 360);
    }

    updateCaption(scene) {
      if (this.captionScene) this.captionScene.textContent = SCENE_LABEL[scene] || '';
      if (this.captionScale) {
        const wallCm = WALL_CM_BY_SCENE[scene] || 280;
        if (this.country === 'us') {
          const ft = (wallCm / 30.48).toFixed(1);
          this.captionScale.textContent = `wall ${ft}ft wide`;
        } else {
          const m = (wallCm / 100).toFixed(1);
          this.captionScale.textContent = `wall ${m}m wide`;
        }
      }
    }

    // ----------------------------------------------------------
    // Room expand/collapse
    // ----------------------------------------------------------
    _syncPreviewToggleLabel() {
      if (!this.toggle) return;
      const span = this.toggle.querySelector('span');
      if (!span) return;
      const show = this.toggle.dataset.fabPreviewShow;
      const hide = this.toggle.dataset.fabPreviewHide;
      if (this.expanded) {
        if (hide != null && hide !== '') span.textContent = hide;
      } else if (show != null && show !== '') {
        span.textContent = show;
      }
    }

    expand() {
      const wrap = this.roomWrap;
      if (!wrap || this.expanded) return;

      window.clearTimeout(this._roomTimer);

      if (this.sceneLayer && !this.sceneLayer.innerHTML.trim() && this.currentScene) {
        this.sceneLayer.innerHTML = sceneHTML(this.currentScene);
      }

      // Make sure no old CSS-var or inline state is fighting us.
      wrap.style.removeProperty('--fab-room-h');
      wrap.style.overflow = 'hidden';
      wrap.style.display = 'block';

      // Measure open height.
      wrap.style.transition = 'none';
      wrap.classList.add('is-expanded');
      wrap.setAttribute('aria-hidden', 'false');
      wrap.style.height = 'auto';

      const target = wrap.scrollHeight;

      // Start collapsed.
      wrap.style.height = '0px';
      void wrap.offsetHeight;

      // Animate open.
      wrap.style.transition = '';
      this.expanded = true;

      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', 'true');
        this._syncPreviewToggleLabel();
      }

      requestAnimationFrame(() => {
        wrap.style.height = target + 'px';
      });

      this._roomTimer = window.setTimeout(() => {
        if (!this.expanded) return;

        wrap.style.height = 'auto';

        if (this.currentParsed) {
          this.applySize(this.currentParsed, false);
        }
      }, 460);
    }

    collapse() {
      const wrap = this.roomWrap;
      if (!wrap || !this.expanded) return;

      window.clearTimeout(this._roomTimer);

      wrap.style.removeProperty('--fab-room-h');
      wrap.style.overflow = 'hidden';
      wrap.style.display = 'block';

      // Pin current open height.
      wrap.style.transition = 'none';
      wrap.style.height = wrap.scrollHeight + 'px';
      void wrap.offsetHeight;

      // Animate closed.
      wrap.style.transition = '';
      wrap.classList.remove('is-expanded');
      wrap.setAttribute('aria-hidden', 'true');

      this.expanded = false;

      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', 'false');
        this._syncPreviewToggleLabel();
      }

      requestAnimationFrame(() => {
        wrap.style.height = '0px';
      });

      this._roomTimer = window.setTimeout(() => {
        if (this.expanded) return;

        wrap.style.height = '';
        wrap.style.display = '';
      }, 460);
    }

    // ----------------------------------------------------------
    // A-sizes toggle
    // ----------------------------------------------------------
    _syncAsizePanelToSelection() {
      if (!this.asizeBlock || !this.asizeToggle) return;
      const selectedCell = this.cells.find(
        c =>
          c.classList.contains('fab-size__asize-cell--selected') ||
          c.classList.contains('fab-size__cell--selected')
      );
      if (!selectedCell) return;
      const isAsize = selectedCell.classList.contains('fab-size__asize-cell');
      if (isAsize) {
        if (!this.asizeExpanded) this.expandAsizes();
      } else if (this.asizeExpanded) {
        this.collapseAsizes();
      }
    }

    toggleAsizes() {
      if (!this.asizeBlock) return;

      if (this.asizeExpanded) {
        this.collapseAsizes();
      } else {
        this.expandAsizes();
      }
    }

    expandAsizes() {
      const block = this.asizeBlock;
      if (!block || this.asizeExpanded) return;

      window.clearTimeout(this._asizeTimer);

      // Critical: remove hidden before measuring, and force display block.
      block.hidden = false;
      block.style.display = 'block';
      block.style.removeProperty('--fab-asize-h');
      block.style.overflow = 'hidden';

      // Measure expanded height.
      block.style.transition = 'none';
      block.classList.add('is-expanded');
      block.style.height = 'auto';

      const target = block.scrollHeight;

      // Start collapsed.
      block.style.height = '0px';
      void block.offsetHeight;

      // Animate open.
      block.style.transition = '';
      this.asizeExpanded = true;

      if (this.asizeToggle) {
        this.asizeToggle.setAttribute('aria-expanded', 'true');
        this.asizeToggle.textContent = this.asizeToggle.dataset.hideLabel || 'Hide A-sizes';
      }

      requestAnimationFrame(() => {
        block.style.height = target + 'px';
      });

      this._asizeTimer = window.setTimeout(() => {
        if (!this.asizeExpanded) return;

        block.style.height = 'auto';
      }, 400);
    }

    collapseAsizes() {
      const block = this.asizeBlock;
      if (!block || !this.asizeExpanded) return;

      window.clearTimeout(this._asizeTimer);

      block.style.display = 'block';
      block.style.removeProperty('--fab-asize-h');
      block.style.overflow = 'hidden';

      // Pin current open height.
      block.style.transition = 'none';
      block.style.height = block.scrollHeight + 'px';
      void block.offsetHeight;

      // Animate closed.
      block.style.transition = '';
      block.classList.remove('is-expanded');

      this.asizeExpanded = false;

      if (this.asizeToggle) {
        this.asizeToggle.setAttribute('aria-expanded', 'false');
        this.asizeToggle.textContent = this.asizeToggle.dataset.showLabel || 'Show A-sizes (A0–A5)';
      }

      requestAnimationFrame(() => {
        block.style.height = '0px';
      });

      this._asizeTimer = window.setTimeout(() => {
        if (this.asizeExpanded) return;

        block.hidden = true;
        block.style.height = '';
        block.style.display = '';
      }, 400);
    }
  }

  // ============================================================
  // Bootstrapping
  // ============================================================
  function initAll(scope) {
    const roots = (scope || document).querySelectorAll('[data-fab-size-picker]');
    roots.forEach(root => {
      if (root.__fabSizePicker) return;
      root.__fabSizePicker = new FabSizePicker(root);
    });
    if (typeof window.__fabInitCollapsedSizePickers === 'function') {
      window.__fabInitCollapsedSizePickers(scope || document);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }
  // Shopify Section Rendering API support — rebind after section re-render
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  // Theme product-info loaded (Dawn-style)
  document.addEventListener('product-info:loaded', () => initAll(document));
})();